<template>
    <v-row>
        <v-col cols="12" md="6">
            <v-card class="h-100">
                <v-card-title>Activity Details</v-card-title>
                <v-card-text>
                    <v-list density="compact">
                        <v-list-item>
                            <template #prepend>
                                <v-icon color="primary">mdi-run</v-icon>
                            </template>
                            <v-list-item-title>Sport</v-list-item-title>
                            <v-list-item-subtitle>{{ sportCategory }}</v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item>
                            <template #prepend>
                                <v-icon color="primary">mdi-clock-outline</v-icon>
                            </template>
                            <v-list-item-title>Duration</v-list-item-title>
                            <v-list-item-subtitle>{{ formattedDuration }}</v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item>
                            <template #prepend>
                                <v-icon color="primary">mdi-gauge</v-icon>
                            </template>
                            <v-list-item-title>Training Stress (TSS)</v-list-item-title>
                            <v-list-item-subtitle>{{ session.trainingLoad != null ? Math.round(session.trainingLoad) : 'N/A' }}</v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item v-if="workoutTypeLabel">
                            <template #prepend>
                                <v-icon color="primary">mdi-lightning-bolt-outline</v-icon>
                            </template>
                            <v-list-item-title>Workout Type</v-list-item-title>
                            <v-list-item-subtitle>
                                <v-chip size="small" color="primary" variant="tonal">{{ workoutTypeLabel }}</v-chip>
                            </v-list-item-subtitle>
                        </v-list-item>
                    </v-list>
                </v-card-text>
            </v-card>
        </v-col>
        <v-col cols="12" md="6">
            <v-card class="h-100">
                <v-card-title>Notes</v-card-title>
                <v-card-text>
                    <div v-if="session.notes" class="text-body-2" style="white-space: pre-wrap;">{{ session.notes }}</div>
                    <div v-else class="text-medium-emphasis text-body-2">No notes.</div>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { categorizeSport } from '@/utils/sportCategory'

interface ManualSession {
    sport?: string | null
    movingTime?: number
    trainingLoad?: number
    workoutType?: string | null
    notes?: string
}

const props = defineProps<{ session: ManualSession }>()

const WORKOUT_TYPE_LABELS: Record<string, string> = {
    recovery: 'Recovery',
    endurance: 'Endurance (Steady-State)',
    tempo: 'Tempo',
    threshold: 'Threshold',
    vo2max: 'VO2max',
    anaerobic: 'Anaerobic',
    strength: 'Strength',
    race: 'Race',
    other: 'Other'
}

const sportCategory = computed(() => categorizeSport('manual', props.session.sport ?? null))
const workoutTypeLabel = computed(() => {
    const type = props.session.workoutType
    if (!type) return ''
    return WORKOUT_TYPE_LABELS[type] || type
})

const formattedDuration = computed(() => {
    const seconds = props.session.movingTime || 0
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.round((seconds % 3600) / 60)
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
})
</script>
